import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import JSZip from "jszip";

const TARGET_COLLECTIONS = [
  "companies",
  "customers",
  "items",
  "invoices",
  "purchases",
  "payments",
  "bankTransactions",
  "bankImports",
  "inventoryMovements",
  "customerPricings"
];

function sanitizeData(val) {
  if (val === null || val === undefined) return val;
  if (typeof val === "object") {
    if (typeof val.toDate === "function") {
      return val.toDate().toISOString();
    }
    if (val.seconds !== undefined && val.nanoseconds !== undefined) {
      return new Date(val.seconds * 1000).toISOString();
    }
    if (Array.isArray(val)) {
      return val.map(sanitizeData);
    }
    const clean = {};
    for (const [k, v] of Object.entries(val)) {
      clean[k] = sanitizeData(v);
    }
    return clean;
  }
  return val;
}

function escapeCsv(val) {
  if (val === null || val === undefined) return "";
  let str = String(val);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    str = `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function toCsv(rows, columns) {
  if (!rows || rows.length === 0) {
    return columns.map(c => c.label).join(",") + "\n";
  }
  const header = columns.map(c => escapeCsv(c.label)).join(",");
  const lines = rows.map(row => {
    return columns.map(c => {
      const val = typeof c.get === "function" ? c.get(row) : row[c.key];
      return escapeCsv(val);
    }).join(",");
  });
  return [header, ...lines].join("\n") + "\n";
}

function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function generateCsvMap(dataMap) {
  const csvs = {};

  // Invoices CSV
  csvs["invoices.csv"] = toCsv(dataMap["invoices"] || [], [
    { label: "Invoice Number", key: "invoiceNumber" },
    { label: "Date", key: "date" },
    { label: "Type", get: r => (r.isNonGst ? "NON-GST" : "GST") },
    { label: "Customer Name", get: r => r.customer?.name || r.customerName || "" },
    { label: "Customer GSTIN", get: r => r.customer?.gstin || "" },
    { label: "Company Name", get: r => r.company?.name || "" },
    { label: "Items Count", get: r => (Array.isArray(r.items) ? r.items.length : 0) },
    { label: "Taxable Amount", key: "taxableAmount" },
    { label: "CGST", key: "cgstAmount" },
    { label: "SGST", key: "sgstAmount" },
    { label: "IGST", key: "igstAmount" },
    { label: "Total Amount", key: "totalAmount" },
    { label: "Status", key: "status" },
    { label: "Notes", key: "notes" }
  ]);

  // Items CSV
  csvs["items.csv"] = toCsv(dataMap["items"] || [], [
    { label: "Item Name", key: "name" },
    { label: "SKU / Code", key: "sku" },
    { label: "HSN Code", key: "hsn" },
    { label: "Unit", key: "unit" },
    { label: "Selling Price", key: "sellingPrice" },
    { label: "MRP", key: "mrp" },
    { label: "Purchase Price", key: "purchasePrice" },
    { label: "GST Rate (%)", key: "gstRate" },
    { label: "Current Stock", key: "currentStock" },
    { label: "Min Stock Alert", key: "minStockLevel" }
  ]);

  // Customers & Parties CSV
  csvs["customers.csv"] = toCsv(dataMap["customers"] || [], [
    { label: "Party Name", key: "name" },
    { label: "Party Type", get: r => (r.partyType || "customer").toUpperCase() },
    { label: "Display / Trade Name", key: "tradeName" },
    { label: "GSTIN", key: "gstin" },
    { label: "Phone", get: r => r.mobile || r.phone || "" },
    { label: "Email", key: "email" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "State Code", key: "stateCode" },
    { label: "Opening Balance", key: "openingBalance" },
    { label: "Bank Name", get: r => r.bankDetails?.bankName || "" },
    { label: "Account Number", get: r => r.bankDetails?.accountNumber || "" },
    { label: "IFSC Code", get: r => r.bankDetails?.ifscCode || "" },
    { label: "UPI ID", get: r => r.bankDetails?.upiId || "" }
  ]);

  // Dedicated Vendors CSV
  const vendorRecords = (dataMap["customers"] || []).filter(r => r.partyType === "vendor" || r.partyType === "both");
  csvs["vendors.csv"] = toCsv(vendorRecords, [
    { label: "Vendor Name", key: "name" },
    { label: "Classification", get: r => (r.partyType || "").toUpperCase() },
    { label: "GSTIN", key: "gstin" },
    { label: "Phone", get: r => r.mobile || r.phone || "" },
    { label: "Email", key: "email" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "Balance To Pay", key: "openingBalance" },
    { label: "Bank Name", get: r => r.bankDetails?.bankName || "" },
    { label: "Account Number", get: r => r.bankDetails?.accountNumber || "" },
    { label: "IFSC Code", get: r => r.bankDetails?.ifscCode || "" },
    { label: "UPI ID", get: r => r.bankDetails?.upiId || "" }
  ]);

  // Companies CSV
  csvs["companies.csv"] = toCsv(dataMap["companies"] || [], [
    { label: "Company Name", key: "name" },
    { label: "Trade Name", key: "tradeName" },
    { label: "GSTIN", key: "gstin" },
    { label: "Phone", key: "phone" },
    { label: "Email", key: "email" },
    { label: "Bank Name", key: "bankName" },
    { label: "Account Number", key: "accountNumber" },
    { label: "IFSC", key: "ifscCode" },
    { label: "GST Next Seq", key: "nextInvoiceNumber" },
    { label: "Non-GST Next Seq", key: "nonGstNextInvoiceNumber" }
  ]);

  return csvs;
}

/**
 * Fetch all collections and their documents from Firestore
 */
export async function fetchAllCollections(onProgress = () => {}) {
  const dataMap = {};
  const counts = {};

  for (let i = 0; i < TARGET_COLLECTIONS.length; i++) {
    const colName = TARGET_COLLECTIONS[i];
    onProgress({ collection: colName, index: i + 1, total: TARGET_COLLECTIONS.length });

    try {
      const snap = await getDocs(collection(db, colName));
      const records = [];

      for (const docSnap of snap.docs) {
        const data = sanitizeData(docSnap.data());
        const record = { id: docSnap.id, ...data };

        // Subcollections check (e.g. shippingAddresses)
        if (colName === "customers") {
          try {
            const subSnap = await getDocs(collection(docSnap.ref, "shippingAddresses"));
            if (!subSnap.empty) {
              record._subcollections = {
                shippingAddresses: subSnap.docs.map(sDoc => ({ id: sDoc.id, ...sanitizeData(sDoc.data()) }))
              };
            }
          } catch (_) {}
        }

        records.push(record);
      }

      dataMap[colName] = records;
      counts[colName] = records.length;
    } catch (err) {
      console.warn(`[Backup] Failed to fetch ${colName}:`, err);
      dataMap[colName] = [];
      counts[colName] = 0;
    }
  }

  return { dataMap, counts };
}

/**
 * Download complete database backup as a ZIP file (JSON + CSV)
 */
export async function downloadFullBackupZip(onProgress = () => {}) {
  onProgress({ stage: "fetching", message: "Fetching database collections..." });
  const { dataMap, counts } = await fetchAllCollections((p) => {
    onProgress({ stage: "fetching", message: `Reading ${p.collection} (${p.index}/${p.total})...` });
  });

  onProgress({ stage: "zipping", message: "Packaging JSON & Excel CSV files into ZIP..." });
  const zip = new JSZip();

  // 1. JSON folder
  const jsonFolder = zip.folder("json");
  for (const [colName, records] of Object.entries(dataMap)) {
    jsonFolder.file(`${colName}.json`, JSON.stringify(records, null, 2));
  }

  // 2. CSV folder
  const csvFolder = zip.folder("csv");
  const csvMap = generateCsvMap(dataMap);
  for (const [fileName, csvStr] of Object.entries(csvMap)) {
    csvFolder.file(fileName, csvStr);
  }

  // 3. Metadata
  const meta = {
    exportedAt: new Date().toISOString(),
    source: "Books & Inventory PWA Manual Backup",
    collectionCounts: counts,
    totalRecords: Object.values(counts).reduce((a, b) => a + b, 0)
  };
  zip.file("backup-meta.json", JSON.stringify(meta, null, 2));

  // Generate blob
  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 9 }
  });

  const dateStr = new Date().toISOString().slice(0, 10);
  const fileName = `books-backup-${dateStr}.zip`;

  triggerDownload(zipBlob, fileName);
  onProgress({ stage: "complete", message: "Backup downloaded successfully!" });

  return { counts, fileName };
}

/**
 * Download a single CSV table (e.g. 'invoices', 'items', 'customers')
 */
export async function downloadSingleCsv(collectionName) {
  const targetCol = collectionName === "vendors" ? "customers" : collectionName;
  const snap = await getDocs(collection(db, targetCol));
  const records = snap.docs.map(d => ({ id: d.id, ...sanitizeData(d.data()) }));
  const dataMap = { [targetCol]: records };
  const csvMap = generateCsvMap(dataMap);
  const fileName = `${collectionName}.csv`;
  const csvContent = csvMap[fileName] || "";

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const dateStr = new Date().toISOString().slice(0, 10);
  triggerDownload(blob, `${collectionName}-${dateStr}.csv`);
}
